
import type { CatalogFieldConfig } from '@empathybroker/tsclient-play/dist/models/instance/catalog-field-config'
import type { Pivot, QueryRankingElementRankingDTO } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { RegularFilterConfig, ToolsMessages } from '../../models'
import { QueryRankingElementRankingAction } from '@empathyco/playboard-core-domain'
import { computed, defineComponent, ref, watch } from 'vue'

import { useFieldValues, useFilters } from '../../composables'

import { ColorEnum, FilterTypeEnum, RestrictedValueEnum } from '../../enums'

import { capitalizeSentence } from '../../filters'
import { RestrictedMixin } from '../../mixins/restricted.mixin'
import { EpbButton } from '../shared/epb-button'
import { EpbSelect } from '../shared/epb-select'

import AttributesRankingList from './attributes-ranking-list.component.vue'

export default defineComponent({
  name: 'AttributesRanking',
  components: {
    EpbButton,
    EpbSelect,
    AttributesRankingList,
  },
  mixins: [RestrictedMixin],
  props: {
    rankingElements: {
      type: Array as PropType<Array<QueryRankingElementRankingDTO>>,
      required: true,
    },
    messages: {
      type: Object as PropType<ToolsMessages['configurations']['fields']['attributes']>,
      required: true,
    },
    pivots: {
      type: Array as PropType<Pivot[]>,
      required: true,
    },
    readOnly: Boolean,
  },
  setup(props) {
    const fieldValuesOptions = ref([])
    const fieldName = ref({ label: '', value: '' })
    const fieldValue = ref('')

    const loading = ref(false)

    const { availableFilters } = useFilters()

    const actionsDisabled = computed((): boolean => {
      return (
        !fieldName.value ||
        !fieldValue.value ||
        props.rankingElements.some(
          (element: QueryRankingElementRankingDTO) =>
            element.fieldValue === fieldValue.value && element.fieldName === fieldName.value.value,
        )
      )
    })

    const addElement = (action: QueryRankingElementRankingAction): void => {
      props.rankingElements.push({
        fieldName: fieldName.value.value,
        fieldValue: fieldValue.value,
        action,
        filters: {},
        schedule: {},
      } as QueryRankingElementRankingDTO)
      fieldName.value = { label: '', value: '' }
      fieldValue.value = ''
    }

    const searchAction = async (): Promise<void> => {
      try {
        loading.value = true

        const { loadFieldValues } = useFieldValues({ reset: false })

        let fieldValues = []

        const pivotsWithOneValue = []

        Object.values(availableFilters.value).forEach(filter => {
          if (
            filter.config.type === FilterTypeEnum.REGULAR &&
            (filter.config as RegularFilterConfig).values.length === 1
          ) {
            pivotsWithOneValue.push({
              name: filter.config.id,
              values: [(filter.config as RegularFilterConfig).values[0].id],
            })
          }
        })

        const pivots = [...pivotsWithOneValue, ...props.pivots]

        if (fieldName.value.value) {
          fieldValues = await loadFieldValues({
            attribute: fieldName.value.value,
            pivots,
          })
        }
        fieldValuesOptions.value = fieldValues.map(fieldValue => ({ value: fieldValue.name }))
      } catch (err) {
        throw err
      } finally {
        loading.value = false
      }
    }

    watch(
      () => fieldName.value.value,
      async () => {
        await searchAction()
      },
    )

    return {
      fieldValuesOptions,
      fieldName,
      fieldValue,
      loading,
      actionsDisabled,
      addElement,
      capitalizeSentence,
    }
  },
  data() {
    return {
      QueryRankingElementRankingAction,
    }
  },
  computed: {
    fieldNameOptions() {
      return this.instanceConfig.catalog.fields.reduce(
        (fieldNameOptions: any[], field: CatalogFieldConfig) => {
          if (field.rankable && !this.disabledFieldIds.includes(field.id)) {
            fieldNameOptions.push({
              value: field.id,
              label: capitalizeSentence(field.prettyName || field.id),
            })
          }
          return fieldNameOptions
        },
        [],
      )
    },
    disabledFieldIds(): string[] {
      return this.instanceConfig.catalog.fields.reduce(
        (disabledFieldIds: string[], field: CatalogFieldConfig) => {
          if (this.getRestrictedValueFromObject(field.restricted) === RestrictedValueEnum.READ) {
            disabledFieldIds.push(field.id)
          }
          return disabledFieldIds
        },
        [],
      )
    },
  },
  methods: {
    getActionColor(action: QueryRankingElementRankingAction): ColorEnum {
      return {
        [QueryRankingElementRankingAction.BOOST]: ColorEnum.GREEN_75,
        [QueryRankingElementRankingAction.BURY]: ColorEnum.RED_75,
      }[action]
    },
    getActionText(action: QueryRankingElementRankingAction): string {
      return {
        [QueryRankingElementRankingAction.BOOST]: this.messages.actions.boost,
        [QueryRankingElementRankingAction.BURY]: this.messages.actions.bury,
      }[action]
    },
  },
})
